import { Flex } from "@chakra-ui/layout";
import { Button, Portal } from "@chakra-ui/react";
import React, { RefObject } from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import { ImportSchoolBoundaryConnectionsSchema } from "../schemas";
import { onImportBoundaryConnectionsCompleteCallback } from "../types";

type Props = {
  onBack: () => void;
  onComplete: onImportBoundaryConnectionsCompleteCallback;
  enrollmentPeriodId: string;
  actionBarRef: RefObject<HTMLDivElement>;
};
export const ImportConnectionsFromJSON: React.FC<Props> = ({
  onBack,
  onComplete,
  enrollmentPeriodId,
  actionBarRef,
}) => {
  const [json, setJson] = React.useState("");
  const [jsonSchema, setJsonSchema] = React.useState<object>({});

  const toast = useAvelaToast({
    containerStyle: {
      marginBottom: "6rem",
    },
  });
  const [isImporting, setImporting] = React.useState(false);
  const service = FSQ.useImportSchoolBoundaryConnections();

  const handleImport = React.useCallback(async () => {
    setImporting(true);
    toast.closeAll();

    try {
      if (json === "") {
        toast.error({
          title: "JSON is required",
          description: "Please provide JSON data to import",
        });
        return;
      }

      // Safely parse and validate JSON against schema before sending to API
      const parseResult = ImportSchoolBoundaryConnectionsSchema.safeParse(
        JSON.parse(json)
      );

      if (!parseResult.success || !parseResult.data) {
        toast.error({
          title: "Invalid JSON",
          description: parseResult.error.errors
            .map((e) => e.message)
            .join(", "),
        });
        return;
      }

      const { data: result } = await service.mutateAsync({
        path: { enrollmentPeriodId: enrollmentPeriodId },
        body: { connections: parseResult.data },
      });

      if (!result || !result?.data) {
        toast.genericError();
        return;
      }

      if (result.data.errors.length > 0) {
        toast.error({
          title: "Boundary connections were not imported",
          description:
            "The boundary connections were not imported due to errors",
        });
        return onComplete({
          data: result.data.connections,
          errors: result.data.errors,
        });
      }

      // Success
      toast({
        title: "Imported from JSON",
        description: "Boundary connections imported from JSON successfully",
        status: "info",
        isClosable: true,
      });

      return onComplete({
        data: result.data.connections,
      });
    } catch (error: any) {
      if (error.error?.message) {
        toast.error({
          title: "Something went wrong",
          description:
            "Unable to import boundary connections. Please check your JSON and try again.",
        });
        console.error("Something went wrong during import from JSON", error);
      } else {
        toast.genericError();
      }
    } finally {
      setImporting(false);
    }
  }, [service, enrollmentPeriodId, json, onComplete, toast]);

  React.useEffect(() => {
    (async () => {
      try {
        // Import the pre-generated form template schema
        const schema = await import(
          "src/generated/import-enrollment-period-school-boundary-connections-schema.json"
        );
        setJsonSchema(schema.default || schema);
      } catch (error) {
        console.error(error);
        setJsonSchema({});
      }
    })();
  }, []);

  return (
    <Flex direction="column" gap={6} grow="1" height="100%">
      <JsonEditor
        schema={jsonSchema}
        text={json}
        onChange={setJson}
        filename="enrollmentPeriodSchoolBoundaryConnections.json"
        editorHeight="fillParent"
      />
      <Portal containerRef={actionBarRef}>
        <Flex justifyContent="space-between">
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              onBack();
            }}
          >
            Back
          </Button>
          <Button isLoading={isImporting} onClick={handleImport}>
            Import
          </Button>
        </Flex>
      </Portal>
    </Flex>
  );
};
